import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/shared/components/empty-state';
import { formatCurrency, formatNumber } from '@/shared/lib/format';

import type { DashboardRevenueRanking } from '../types/dashboard.types';

/**
 * A top-5 list by completed-order revenue. The backend ranks within each
 * currency, so rows are shown per currency and never compared across them.
 */
export function RevenueRankingCard({
  title,
  rows,
  hrefFor,
}: {
  title: string;
  rows: DashboardRevenueRanking[];
  /** Where a row links to; rows are plain text without it. */
  hrefFor?: (row: DashboardRevenueRanking) => string;
}) {
  const currencies = [...new Set(rows.map((row) => row.currencyCode))];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 ? (
          <EmptyState title="No completed orders in this period" className="h-24" />
        ) : (
          currencies.map((currency) => (
            <section key={currency} aria-label={`${title} in ${currency}`}>
              {currencies.length > 1 ? (
                <p className="text-muted-foreground mb-1 text-[11px] font-medium uppercase">
                  {currency}
                </p>
              ) : null}
              <ol className="divide-y">
                {rows
                  .filter((row) => row.currencyCode === currency)
                  .map((row, index) => (
                    <li
                      key={row.id}
                      className="flex items-center justify-between gap-3 py-1.5 text-sm"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="text-muted-foreground w-4 shrink-0 text-xs tabular-nums">
                          {index + 1}
                        </span>
                        {hrefFor ? (
                          <Link to={hrefFor(row)} className="text-primary truncate hover:underline">
                            {row.name}
                          </Link>
                        ) : (
                          <span className="truncate">{row.name}</span>
                        )}
                      </span>
                      <span className="flex shrink-0 items-baseline gap-2">
                        <span className="text-muted-foreground text-xs tabular-nums">
                          {formatNumber(row.orders)} orders
                        </span>
                        <span className="font-medium tabular-nums">
                          {formatCurrency(row.revenue, row.currencyCode)}
                        </span>
                      </span>
                    </li>
                  ))}
              </ol>
            </section>
          ))
        )}
      </CardContent>
    </Card>
  );
}
